import { AutocompleteOption } from "components/Form/components/Autocomplete/Autocomplete";
import { IconCollection } from "components/Icon/IconCollection";
import { FeConfigType } from "config/config";
import { getBundles } from "containers/Bundles/Bundles.selectors";
import { getProjects } from "containers/Projects/Projects.selectors";
import { getWorkspaceData } from "containers/Workspaces/Workspaces.selectors";
import { Bundle, ContainerType, Project, Workspace } from "graphql/generated/graphql";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { sortAlphabetically } from "utils/sorting";
import { checkBundleItemType, checkProjectItemType, checkWorkspaceItemType } from "utils/utils";

type ContainerWithType<T> = T & { type: Lowercase<ContainerType> };

export const useMoveToLocation = (
    singleResourceType?: FeConfigType,
    resourceLocationId?: string
) => {
    const projectList: ContainerWithType<Project>[] = useSelector(getProjects).map(
        (project: Project) => ({
            ...project,
            type: "project",
        })
    );
    const bundleList: ContainerWithType<Bundle>[] = useSelector(getBundles).map(
        (bundle: Bundle) => ({
            ...bundle,
            type: "bundle",
        })
    );
    const workspaceList: ContainerWithType<Workspace>[] = [useSelector(getWorkspaceData)].map(
        (workspace: Workspace) => ({
            ...workspace,
            type: "workspace",
        })
    );

    const getOptionsList = useCallback(
        (
            list:
                | ContainerWithType<Project>[]
                | ContainerWithType<Bundle>[]
                | ContainerWithType<Workspace>[]
        ): AutocompleteOption[] =>
            sortAlphabetically([...list], (listItem) => listItem.name).map((location) => ({
                value: location.id,
                label: location.name,
                icon: location.icon,
                is_readonly: location.is_readonly,
                type: location.type,
            })),
        []
    );

    const projectOptionsList = useMemo(
        () => getOptionsList(projectList),
        [getOptionsList, projectList]
    );
    const bundleOptionsList = useMemo(
        () => getOptionsList(bundleList),
        [bundleList, getOptionsList]
    );
    const workspaceOptionsList = useMemo(
        () => getOptionsList(workspaceList),
        [workspaceList, getOptionsList]
    );

    const groupOptions = useMemo(() => {
        const resourceType = singleResourceType?.toUpperCase();

        const options: { label: string; options: AutocompleteOption[] }[] = [];
        if (!resourceType || checkWorkspaceItemType(resourceType)) {
            options.push({
                label: "Workspace",
                options: workspaceOptionsList,
            });
        }
        if (!resourceType || checkProjectItemType(resourceType)) {
            options.push({
                label: "Projects",
                options: projectOptionsList,
            });
        }
        if (!resourceType || checkBundleItemType(resourceType)) {
            options.push({ label: "Bundles", options: bundleOptionsList });
        }

        return options;
    }, [bundleOptionsList, projectOptionsList, singleResourceType, workspaceOptionsList]);

    const flatList = useMemo(
        () => groupOptions.map((locationType) => locationType.options).flat(),
        [groupOptions]
    );
    const initialLocation = flatList.find((location) => location.value === resourceLocationId);

    const [selectedLocation, setSelectedLocation] = useState<AutocompleteOption | undefined>(
        initialLocation
    );

    const options = {
        group: { groupOptions, flatList },
        iconReplacement: IconCollection.folderTableOutline,
    };

    const changeValue = useCallback(
        (resourceId: string) => {
            const thisResource = flatList.find((location) => location.value === resourceId);
            setSelectedLocation(thisResource);
        },
        [flatList]
    );

    const isSameLocation = initialLocation?.value === selectedLocation?.value;
    return {
        selectedLocation,
        changeValue,
        options,
        isSameLocation,
    };
};
